import React, { useRef, useEffect, useState } from 'react';
import '../../styles/ModuloAnagrafico.css';

/**
 * FirmaCanvas
 * Riquadro di firma su canvas nativo, senza dipendenze npm.
 * Usa Pointer Events: funziona con mouse, touch e penna (tablet Wacom).
 *
 * LA TELA SEGUE `valore`, E NON E' UN DETTAGLIO
 * Il dimensionamento e il ripristino stavano in un effetto con le
 * dipendenze vuote, cioe' al solo montaggio. Nel wizard i quattro moduli
 * si susseguono nella stessa posizione dell'albero, quindi React riusava
 * la stessa istanza e la tela non veniva rimontata: alla pagina della
 * privacy compariva la firma dell'anagrafica, gia' disegnata sui pixel,
 * mentre `firme.privacy` era ancora vuoto. Chi firmava sopra consegnava i
 * due tratti sovrapposti; chi premeva «Cancella» azzerava una firma che
 * per il programma non c'era.
 *
 * Ora l'effetto guarda `valore`: se arriva vuoto la tela si pulisce, se
 * arriva una firma la ridisegna. Il tratto appena fatto non si ridisegna
 * da solo - si tiene da parte cio' che si e' emesso, altrimenti ogni
 * pennellata farebbe un giro di ridisegno sfocando la precedente.
 *
 * Props:
 *  - valore      dataURL della firma gia' acquisita (per ripristinarla)
 *  - onChange    (dataURL | null) => void
 *  - errore      messaggio di errore da evidenziare
 *  - etichetta   testo sotto il riquadro
 */

const FirmaCanvas = ({ valore, onChange, errore, etichetta }) => {
    const canvasRef = useRef(null);
    const disegnaRef = useRef(false);
    // Cio' che questa tela ha appena prodotto: non va ridisegnato
    const emessoRef = useRef(null);
    const [presente, setPresente] = useState(!!valore);

    /* Dimensionamento e allineamento della tela a `valore` */
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ratio = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext('2d');

        /* Assegnare width o height azzera la tela, quindi si fa solo
           quando la misura e' cambiata davvero: farlo a ogni giro
           cancellerebbe il tratto in corso. */
        if (canvas.width !== Math.round(rect.width * ratio)
            || canvas.height !== Math.round(rect.height * ratio)) {
            canvas.width = Math.round(rect.width * ratio);
            canvas.height = Math.round(rect.height * ratio);
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(ratio, ratio);
        }
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#1a1a2e';

        // Il tratto che abbiamo appena emesso e' gia' sulla tela
        if (valore && valore === emessoRef.current) return;

        if (!valore) {
            ctx.clearRect(0, 0, rect.width, rect.height);
            emessoRef.current = null;
            setPresente(false);
            return;
        }

        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, rect.width, rect.height);
            ctx.drawImage(img, 0, 0, rect.width, rect.height);
        };
        img.src = valore;
        emessoRef.current = valore;
        setPresente(true);
    }, [valore]);

    const coordinate = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const iniziaTratto = (e) => {
        e.preventDefault();
        canvasRef.current.setPointerCapture(e.pointerId);
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = coordinate(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
        disegnaRef.current = true;
    };

    const continuaTratto = (e) => {
        if (!disegnaRef.current) return;
        e.preventDefault();
        const ctx = canvasRef.current.getContext('2d');
        const { x, y } = coordinate(e);
        ctx.lineTo(x, y);
        ctx.stroke();
        if (!presente) setPresente(true);
    };

    const chiudiTratto = (e) => {
        if (!disegnaRef.current) return;
        e.preventDefault();
        disegnaRef.current = false;
        const dataUrl = canvasRef.current.toDataURL('image/png');
        emessoRef.current = dataUrl;
        if (onChange) onChange(dataUrl);
    };

    const cancella = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const ratio = window.devicePixelRatio || 1;
        ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
        emessoRef.current = null;
        setPresente(false);
        if (onChange) onChange(null);
    };

    return (
        <div className="blocco-firma">
            <div className={`riquadro-firma ${errore ? 'errore' : ''}`}>
                <canvas
                    ref={canvasRef}
                    className="canvas-firma"
                    onPointerDown={iniziaTratto}
                    onPointerMove={continuaTratto}
                    onPointerUp={chiudiTratto}
                    onPointerLeave={chiudiTratto}
                    onPointerCancel={chiudiTratto}
                />
                {!presente && <span className="segnaposto-firma">Firma qui</span>}
            </div>
            <div className="barra-firma">
                <span className="data-firma">
                    {etichetta || 'Santarcangelo di Romagna, ' + new Date().toLocaleDateString('it-IT')}
                </span>
                <button type="button" className="btn-testo" onClick={cancella}>
                    Cancella firma
                </button>
            </div>
            {errore && <span className="messaggio-errore">{errore}</span>}
        </div>
    );
};

export default FirmaCanvas;
